import EventEmitter from 'wolfy87-eventemitter'
import _ from 'lodash'
import Queue from 'promise-queue'
import {getStr, replace, trim, trimEnd, trimStart} from 'eyes-lib/module/string'
import {Ajax, CSSController} from 'eyes-lib/module/web'
import {Store} from 'eyes-lib/module/model'
import {foundPath} from 'eyes-lib/module/basic'
import Cookies from 'js-cookie'
import moment from 'moment'
import getPseudoContent from 'get-pseudo-content'
import XXH from 'xxhashjs'
import {urlInfo} from 'eyes-lib/module/util'

/**
 * ## 動作説明
 * 1. DOMから翻訳処理したい要素を抽出（Translate.collectTranslateIte関数）
 * 2. 辞書・APIを使った置換処理を適用していく（Translate.translate関数）
 *
 * * イベントを発行しているので、翻訳終了イベントを使い、処理を発火させることも可能です。(EVENTS定数参照)
 * * 言語設定はブラウザ・URL等から取得可能で、クッキー・ストレージに保存することも可能です。(languageオブジェクト参照)
 * ! 機能追加がある場合はテストコードを作成し、テストを通す
 */

/**
 * 翻訳対象となるセレクタ設定
 */
const SELECTOR = {
	STATIC: '.multiconv',
	DYNAMIC: '.apimulticonv',
	NON_TARGET: '.no-multiconv',
	ALL: `div,span,a,h1,h2,h3,h4,h5,h6,p,label,th,td,li,dt,dd,caption,title,em,strong,font,i,strong,small,sub,sup,option,input,caption,textarea`,
}

/**
 * イベント定数
 * @description イベント駆動に対応
 */
const EVENTS = Object.freeze({
	READY: 'multi-lang:ready',
	START: 'multi-lang:start',
	END: 'multi-lang:end',
})

/** ノードの型を定義 */
const NodeTypes = Object.freeze({
	alt: 'NodeTypes.alt',
	value: 'NodeTypes.value',
	title: 'NodeTypes.title',
	placeholder: 'NodeTypes.placeholder',
	TextNode: 'NodeTypes.TextNode',
	before: 'NodeTypes.before',
	after: 'NodeTypes.after',
})

/** 言語保存キー */
const LANGUAGE_STORE_KEY = '#use_language'

/** PromiseをQueue実行するモジュール */
const queue = new Queue(2, 256)

/**
 * 辞書ファイルのパス
 * @description `setDictionaryPath`を使って変更する
 */
let DICTIONARY_PATH = ''

/**
 * 辞書APIのURL
 * @description `apiConfig`を使って変更する
 */
const MultiApi = {
	URL: '',
	USER: '',
}

/**
 * 翻訳辞書
 * @type {MultiLanguageDataFormat[]}
 */
const JSON_DIC = []

/** イベント駆動用オブジェクト */
const event = new EventEmitter()

/** スタイルキャッシュ */
const StyleCache = {}

/**
 * 言語情報を取得・格納する手段を提供
 */
const language = {
	value: undefined,
	key: LANGUAGE_STORE_KEY,
	storage: new Store({
		storage: localStorage,
		namespace: LANGUAGE_STORE_KEY,
		expireMs: moment().add(12, 'month').diff(moment()),
	}),
	session: new Store({
		storage: sessionStorage,
		namespace: LANGUAGE_STORE_KEY,
		expireMs: 1000 * 60 * 60 * 12,
	}),
	/**
	 * ブラウザから言語情報を取得する
	 * @returns {string}
	 */
	getByBrowser: function () {
		try {
			const lang = foundPath(navigator, 'language', 'userLanguage', 'browserLanguage').substr(0, 2).toLowerCase()
			return lang ? lang.toLowerCase() : undefined
		} catch (err) {
			return undefined
		}
	},
	/**
	 * 国コードから言語コードを返す
	 * @param {string} code
	 */
	getByCountryCode: function (code) {
		let lang = code
		switch (code) {
			case 'ja':
			case 'jp':
			case 'jpn': {
				lang = 'ja'
				break
			}
			case 'cn':
			case 'mo':
			case 'tw':
			case 'hk': {
				lang = 'zh'
				break
			}
			case 'us':
			case 'ca':
			case 'au':
			case 'en':
			case 'gb': {
				lang = 'en'
				break
			}
			case 'br':
			case 'pt': {
				lang = 'pt'
				break
			}
			case 'ko':
			case 'kr': {
				lang = 'ko'
				break
			}
			default:
				break
		}
		return lang
	},
	/**
	 * URLパス情報から言語情報を取得する
	 * @description プロジェクトの多言語URL仕様にあわせて正規表現を編集してください
	 * @param {string|URL} url
	 * @param {boolean} [fuzzy=false] 曖昧・表記ゆれフラグ
	 */
	getByURL: function (url = _.get(location, 'href'), fuzzy = false) {
		/** @type {string} */
		const value = _.get(
			`${url}`.match(/\/(ja|jp|jpn|en|us|gb|ca|au|mo|tw|hk|cn|zh|fr|de|ko|es|ko|cn|ru|it|ar|kr|br|pt)\//i),
			'1',
		)

		const lang = value ? value.toLowerCase() : undefined

		/** 表記ゆれ（国コード）対応 */
		if (fuzzy) {
			return language.getByCountryCode(lang)
		}
		return lang
	},

	/**
	 * クッキーから言語情報を取得する
	 */
	getByCookie: function () {
		return Cookies.get(LANGUAGE_STORE_KEY)
	},
	/**
	 * クッキーに言語情報を格納する
	 * @param {string} lang
	 */
	setToCookie: function (lang) {
		Cookies.set(LANGUAGE_STORE_KEY, lang)
		const value = Cookies.get(LANGUAGE_STORE_KEY)
		return lang === value ? value : false
	},

	/**
	 * ローカルストレージから言語情報を取得する
	 */
	getByStorage: function () {
		//@ts-ignore
		return language.storage.getItem(LANGUAGE_STORE_KEY)
	},

	/**
	 * ローカルストレージに言語情報を格納する
	 * @param {string} lang
	 */
	setToStorage: function (lang) {
		//@ts-ignore
		language.storage.setItem(LANGUAGE_STORE_KEY, lang)
		const value = this.getByStorage()
		return value === lang ? value : false
	},

	/**
	 * ローカルストレージから言語情報を取得する
	 */
	getBySession: function () {
		//@ts-ignore
		return language.session.getItem(LANGUAGE_STORE_KEY)
	},
	/**
	 * ローカルストレージに言語情報を格納する
	 * @param {string} lang
	 */
	setToSession: function (lang) {
		//@ts-ignore
		language.session.setItem(LANGUAGE_STORE_KEY, lang)
		const value = this.getBySession()
		return value === lang ? value : false
	},

	/**
	 * 各プロジェクトで決められたデフォルトの動作で言語情報を取得する関数
	 * @description 各プロジェクトの仕様にあわせること
	 * @returns {string} 2レター言語コード
	 */
	getDefaultLang: function () {
		return (
			language.value ||
			language.getByStorage() ||
			language.getByCookie() ||
			language.getByURL() ||
			language.getByBrowser() ||
			'ja'
		)
	},
}

/** ヘルパー関数定義 */
const helper = {
	language,
	/**
	 * コンソールログ出力
	 * @param {TranslateResultItem[]} result
	 */
	consoleLog: function (result) {
		_.each(result, (item) => {
			/** ログ出力はノンブロッキングで実行 */
			queue.add(async () => {
				console.log(item.before, '→', item.after)
			})
		})
	},
	/**
	 * URL文字列から商品コードを取得する
	 * @description
	 * @param {string} value URL - App.REST_PATHを投げるのが良い
	 */
	getTourcode: function (value = location.href) {
		const matchCode = value.match(/\/([a-z0-9*]{12})\/[a-z0-9*]{2}\/[0-9]{8}\//i)
		// console.log(matchCode)
		if (_.size(matchCode) > 1) return _.get(matchCode, 1)

		const params = urlInfo(value).param
		const tourCode = _.chain(params).pick(['c1', 'c2', 'c3']).values().join('').value()
		// console.log(tourCode)
		return tourCode ? tourCode : undefined
	},
	/**
	 * CSSセレクタのクラス指定文字列から純粋なクラス名を取得
	 * @description つまり `.item` → `item` となる
	 * @param {string} selector
	 */
	className: function (selector) {
		return trimStart(selector, '.')
	},
}

/** Input要素のタイプをチェックする関数が入ってる */
const InputElementChecker = Object.freeze({
	/**
	 * placeholderを翻訳対象とさせたいINPUTタグ
	 * @param {Element} elem
	 */
	hasPlaceholderAttr: function (elem) {
		return getStr(elem, 'placeholder').length > 0
	},

	/**
	 * valueを翻訳対象とさせたいINPUTタグ
	 * @param {Element} elem
	 */
	hasValueAttr: function (elem) {
		return /^button|reset|submit$/i.test(getStr(elem, 'type')) && getStr(elem, 'value').length > 0
	},

	/**
	 * altを翻訳対象とさせたいINPUTタグ
	 * @param {Element} elem
	 */
	hasAltAttr: function (elem) {
		return /^image$/i.test(getStr(elem, 'type')) && getStr(elem, 'alt').length > 0
	},
})

/** 翻訳対象ノードフィルター */
const filters = {
	/**
	 * 翻訳対象となるタグのみ通すフィルター
	 * @param {Element} elem
	 */
	TagFilter: function (elem) {
		if (_.hasIn(elem, 'nodeName'))
			return !_.includes(['HEAD', 'BODY', 'META', 'SCRIPT', 'LINK', 'BR', 'HEADER', 'FORM'], elem.nodeName)

		return false
	},

	/**
	 * 翻訳させないクラスがあるか親要素を調べるフィルター
	 * @description 親にno-multiconvクラスがあれば除去
	 * @param {Element} elem
	 */
	ClassFilter: function (elem) {
		return !elem.closest(SELECTOR.NON_TARGET)
	},

	/**
	 * 属性が翻訳対象となるタグを通すフィルタ
	 * @param {Element} elem
	 */
	AttributeFilter: function (elem) {
		if (elem instanceof HTMLInputElement) {
			return (
				InputElementChecker.hasPlaceholderAttr(elem) ||
				InputElementChecker.hasValueAttr(elem) ||
				InputElementChecker.hasAltAttr(elem)
			)
		} else if (elem instanceof HTMLImageElement) {
			return getStr(elem, 'alt').length > 0
		} else {
			return getStr(elem, 'title').length > 0
		}
	},

	/**
	 * 空文字ではないノードがあれば通す
	 * @param {Element} elem
	 */
	NodeFilter: function (elem) {
		return _.some(elem.childNodes, (node) => getStr(node, 'nodeValue').length > 0)
	},

	/**
	 * ::before疑似要素にcontentがあれば通す
	 * @param {Element} elem
	 */
	CSSBeforeAfterFilter: function (elem) {
		return !!getPseudoContent(elem, '::before')
	},
	/**
	 * ::after疑似要素にcontentがあれば通す
	 * @param {Element} elem
	 */
	CSSAfterFilter: function (elem) {
		return !!getPseudoContent(elem, '::after')
	},
}

/** 内部関数 */
const inner = {
	/**
	 * 辞書JSONから翻訳辞書を取得する
	 * @param {TranslateApiOptions} options
	 */
	fetchByJson: function ({lang, name, ext}) {
		/** 拡張子が設定されているならばドットをつける */
		const extension = ext ? `.${ext}` : ''

		if (!name || !lang) return Promise.resolve([])

		return (
			Ajax.load({
				// 翻訳辞書jsonのアドレスを設定
				url: `${DICTIONARY_PATH}/${lang}/${name}${extension}`,
				type: 'GET',
				dataType: ext,
				cache: false,
				error: () => Promise.resolve([]),
			})
				.then((json) => {
					/** 静的に辞書に追加する場合、ここで操作 */
					// json.push( {
					// 	jp: '池袋',
					// 	multi: 'Ikebukuro',
					// } )
					return json
				})
				/** 日本語長い文字順ソート */
				.then((json) => _.sortBy(json, (item) => _.get(item, 'jp.length', 0) * -1))
				.then((json) => {
					/** メモリ化 */
					Translate.setDictionary({lang, name}, json)
					return json
				})
				.catch((err) => [])
		)
	},
	/**
	 * メモリから翻訳辞書を取得する
	 * @param {TranslateApiOptions} options
	 */
	fetchByMemory: function ({lang, name}) {
		return Promise.resolve(_.get(JSON_DIC, `${lang}_${name}`))
	},
	/**
	 * APIから翻訳辞書を取得する
	 * @param {MultiApiOptions} options
	 * @returns {Promise<string>}
	 */
	fetchByApi: function (options) {
		// return Promise.resolve("test")
		if (!MultiApi.URL || !options.langCd || !options.name || !options.user) return Promise.resolve(options.name)

		return Ajax.load({
			// 翻訳辞書jsonのアドレスを設定
			url: MultiApi.URL,
			contentType: `application/json`,
			type: 'POST',
			dataType: 'json',
			cache: false,
			data: JSON.stringify(options),
		})
			.then((json) => {
				let text = _.get(json, 'multiList.0.name_mt', '')
				if (!text) return options.name
				return text
			})
			.catch((err) => options.name)
	},
	/**
	 * 疑似要素のcontentを書き換える
	 * @param {Element} elem
	 * @param {'::before'|'::after'} pseudo
	 * @param {string} content
	 */
	setPseudoContent: function (elem, pseudo, content) {
		/** ID生成関数 */
		function genId(val) {
			/** IDの頭は文字列であること */
			if ('::before' === pseudo) return 'bf' + XXH.h32(val, 0xabcd).toString(16)
			else if ('::after' === pseudo) return 'af' + XXH.h32(val, 0xabcd).toString(16)
		}

		/** ID取得（なければ生成） */
		const id = elem.id ? elem.id : genId(content)

		/** 要素にIDが設定されていなければ、セットする */
		if (!elem.id) _.set(elem, 'id', id)

		/** セレクタ定義 */
		const selector = `#${id}${pseudo}`

		/** メモを調べ、すでにスタイル追加済みならば処理抜ける */
		if (_.has(StyleCache, selector)) return selector //! EXIT

		/** スタイルをheadタグ内に追記し、contentを上書き */
		CSSController.addRule(selector, `content`, `"${content}"`)

		/** 追記済みスタイルをメモ */
		_.set(StyleCache, selector, true)

		return selector
	},
	/**
	 * DOM置換処理
	 * @param {TranslateItem} item
	 */
	domReplacer: function (item) {
		/** 要素を翻訳 */
		if (item.elem instanceof HTMLInputElement) {
			if (item.type === NodeTypes.placeholder) _.set(item, 'elem.placeholder', item.text)
			if (item.type === NodeTypes.alt) _.set(item, 'elem.alt', item.text)
			if (item.type === NodeTypes.value) _.set(item, 'elem.value', item.text)
		} else if (item.elem instanceof HTMLImageElement) {
			if (item.type === NodeTypes.alt) _.set(item, 'elem.alt', item.text)
		} else if (item.type === NodeTypes.TextNode) {
			_.set(item, 'node.nodeValue', item.text)
		}

		/** 疑似要素を翻訳 */
		if (item.type === NodeTypes.before) {
			inner.setPseudoContent(item.elem, '::before', item.text)
		} else if (item.type === NodeTypes.after) {
			inner.setPseudoContent(item.elem, '::after', item.text)
		}

		// console.log('変換：', `"${item.before}" → "${item.after}"`, `「${item.text}」`);
		/** シリアライズした処理結果を作成 */
		return {
			text: `${item.text}`,
			before: `${item.before}`,
			after: `${item.after}`,
		}
	},
}

/**
 *
 */
class TranslateItem {
	/**
	 * 翻訳用アイテム
	 * @param {TranslateItemOptions} params
	 */
	constructor({type, elem, node, text, dynamic = false}) {
		/** 翻訳オブジェクトのタイプ */
		this.type = type
		this.elem = elem
		this.node = node
		this.text = text
		this.dynamic = dynamic
		/** 翻訳前 */
		this.before = `${text}`
		/** 翻訳後(あくまでもロギング用) */
		this.after = null
		/** 翻訳済みかどうか */
		this.resolved = false
	}
}

const Translate = {
	event,
	/** 言語識別子ヘルパー関数 */
	language,
	/** ヘルパー関数 */
	helper,
	/** 対象抽出フィルター関数 */
	filters,
	/** 内部関数 */
	inner,
	/** @param {string} value */
	setDictionaryPath: function (value) {
		DICTIONARY_PATH = trimEnd(value, '/')
		event.emit(EVENTS.READY)
		return Translate
	},
	getDictionaryPath: function () {
		return DICTIONARY_PATH
	},
	/**
	 * 翻訳辞書をセットする
	 * @param {TranslateApiOptions} options
	 * @param {MultiLanguageDataFormat[]} dic
	 */
	setDictionary: function (options, dic) {
		_.set(JSON_DIC, `${options.lang}_${options.name}`, dic)
		return Translate
	},
	/**
	 * API翻訳のURLをセットする
	 * @param {{url:string|URL,user:string}} config
	 */
	apiConfig: function (config) {
		Object.assign(MultiApi, {
			URL: `${config.url}`,
			USER: config.user,
		})
		event.emit(EVENTS.READY)
		return Translate
	},
	/**
	 * 翻訳ノードを集める
	 * @param {Element|Element[]|Document} [container=document]
	 * @returns {TranslateItem[]}
	 */
	collectTranslateItem: function (container = document) {
		const targetList = (() => {
			/** @type {Element[]} */
			const list = []

			_.chain(container)
				.castArray()
				/** JSON辞書翻訳の要素を抽出 */
				.each(
					/** @param {Element|Document} con */
					(con) => {
						const elems = con.querySelectorAll(SELECTOR.STATIC)
						_.each(elems, (elem) => list.push(elem))
					},
				)
				/** API翻訳の要素を抽出 */
				.each(
					/** @param {Element|Document} con */
					(con) => {
						const elems = con.querySelectorAll(SELECTOR.DYNAMIC)
						_.each(elems, (elem) => list.push(elem))
					},
				)
				.value()

			return list
		})()

		return (
			_.chain(targetList)
				/** 予め余分なタグを除去 */
				.filter(filters.TagFilter)
				.filter(filters.ClassFilter)
				.filter(
					(elem) =>
						filters.AttributeFilter(elem) ||
						filters.NodeFilter(elem) ||
						filters.CSSBeforeAfterFilter(elem) ||
						filters.CSSAfterFilter(elem),
				)
				.reduce(
					/**
					 * 翻訳用アイテムを生成していく
					 * @param {TranslateItem[]} acc
					 */
					(acc, elem) => {
						/** API翻訳かどうか */
						const dynamic = elem.classList.contains(helper.className(SELECTOR.DYNAMIC))
						// console.log(elem.textContent, dynamic)

						if (elem instanceof HTMLInputElement) {
							if (InputElementChecker.hasPlaceholderAttr(elem)) {
								acc.push(
									new TranslateItem({
										type: NodeTypes.placeholder,
										elem,
										node: null,
										text: getStr(elem, 'placeholder'),
										dynamic,
									}),
								)
							}

							if (InputElementChecker.hasValueAttr(elem)) {
								acc.push(
									new TranslateItem({
										type: NodeTypes.value,
										elem,
										node: null,
										text: getStr(elem, 'value'),
										dynamic,
									}),
								)
							}

							if (InputElementChecker.hasAltAttr(elem)) {
								acc.push(
									new TranslateItem({
										type: NodeTypes.alt,
										elem,
										node: null,
										text: getStr(elem, 'alt'),
										dynamic,
									}),
								)
							}
						} else if (elem instanceof HTMLImageElement) {
							if (getStr(elem, 'alt').length) {
								acc.push(
									new TranslateItem({
										type: NodeTypes.alt,
										elem,
										node: null,
										text: getStr(elem, 'alt'),
										dynamic,
									}),
								)
							}
						}

						if (getStr(elem, 'title').length > 0) {
							//空文字ではないノードを溜め込む
							acc.push(
								new TranslateItem({
									type: NodeTypes.title,
									elem,
									node: null,
									text: getStr(elem, 'title'),
									dynamic,
								}),
							)
						}

						const before = getPseudoContent(elem, '::before')
						if (!!before) {
							//before疑似要素
							acc.push(
								new TranslateItem({
									type: NodeTypes.before,
									elem,
									node: null,
									text: before,
									dynamic,
								}),
							)
						}

						const after = getPseudoContent(elem, '::after')
						if (!!after) {
							//after疑似要素
							acc.push(
								new TranslateItem({
									type: NodeTypes.after,
									elem,
									node: null,
									text: after,
									dynamic,
								}),
							)
						}

						/** 参照ノードとテキストを格納 */
						_.each(elem.childNodes, (node) => {
							if (getStr(node, 'nodeValue').length > 0) {
								//空文字ではないノードを溜め込む
								acc.push(
									new TranslateItem({
										type: NodeTypes.TextNode,
										elem,
										node,
										text: getStr(node, 'nodeValue'),
										dynamic,
									}),
								)
							}
						})

						return acc
					},
					[],
				)
				.value()
		)
	},
	/**
	 * 翻訳実行(インターフェース)
	 * @description その他の翻訳実装が追加された時
	 * メソッド名が変わらないようにここで吸収
	 * @async
	 * @param {TranslateApiOptions} options
	 * @param {TranslateItem[]} transItems
	 */
	translate: async function (options, transItems) {
		/** その他の翻訳実装を仕分ける場合を想定し、ワンクッションおいてます */
		const splitItems = _.groupBy(transItems, 'dynamic')
		const dynamicApi = _.get(splitItems, 'true')
		const staticJson = _.get(splitItems, 'false')

		const logItems = await Promise.all([
			Translate.translateApi(options, dynamicApi),
			Translate.translateEyes(options, staticJson),
		])
		return logItems[0].concat(logItems[1])
	},
	/**
	 * 動的翻訳
	 * @async
	 * @param {TranslateApiOptions} options
	 * @param {TranslateItem[]} transItems
	 */
	translateApi: async function (options, transItems) {
		/** 翻訳辞書がnullならRESTで取得 */
		const jobs = _.map(transItems, async (item) => {
			/** APIパラメータ */
			const params = {
				user: MultiApi.USER,
				langCd: `${options.lang || ''}`.toUpperCase(),
				name: item.text,
			}
			/** APIコール */
			const res = await inner.fetchByApi(params)

			/** @type {TranslateItem} */
			const resItem = Object.assign({}, item, {
				text: res,
				after: res,
				resolved: true,
			})

			/** 結果をDOM置換処理に投げる */
			return inner.domReplacer(resItem)
		})

		/** 処理待ち */
		const res = await Promise.all(jobs)

		return res
	},
	/**
	 * Eyes実装（静的json）の翻訳
	 * @async
	 * @param {TranslateApiOptions} options
	 * @param {TranslateItem[]} transItems
	 */
	translateEyes: function (options, transItems) {
		/** 翻訳辞書がnullならRESTで取得 */
		const fetchDic = _.has(JSON_DIC, `${options.lang}_${options.name}`) ? inner.fetchByMemory : inner.fetchByJson

		return fetchDic(options).then((json) => {
			/**
			 * 翻訳定義をすべて処理する
			 * @description 文字数の長いものからマッチングさせたいので
			 * 翻訳辞書ベースでループさせ、置換処理を行っている
			 */
			const result = _.map(json, (dic) => {
				//文字数を格納(キャッシュ化)
				const textSize = dic.jp.length

				//対象要素を処理します
				return (
					_.chain(transItems)
						/** 変換済み(resolved=true)は除外 */
						.filter((item) => false === item.resolved)
						/** 文字数でフィルタリング */
						.filter((item) => textSize <= getStr(item, 'text').length)
						/** 変換できた文字列のみ次のstreamへ渡す(memにpush) */
						.reduce(
							/** @param {TranslateItem[]} acc */
							(acc, item) => {
								// console.info( item.jp, elemObj.text )
								const srcText = item.text
								if (!srcText.includes(dic.jp)) {
									/** 含まれていない場合、次へ */
									return acc //!NEXT
								}

								/** 含まれている場合、置換 */
								const transText = replace(srcText).replace(dic.jp, dic.multi).value()

								/** なにかしら置換されたならば */
								if (srcText != transText) {
									item.text = transText
									item.after = transText
									item.resolved = srcText === dic.jp //まったく同じならば変換完了
									acc.push(item) //追加
								}

								return acc //!NEXT
							},
							[],
						)
						/** 変換された文字列を機械的に入れ替えていく */
						.map(inner.domReplacer)
						.value()
				)
			})

			return _.flatten(result)
		})
	},
	/**
	 * 翻訳実行
	 * @param {TranslateApiOptions} options
	 */
	execute: async function (options) {
		event.emit(EVENTS.START)

		/** @type {TranslateApiOptions} */
		const def = {
			log: false,
			lang: 'jp',
			ext: 'json',
			name: undefined,
			container: document,
			selector: [SELECTOR.STATIC, SELECTOR.DYNAMIC],
		}

		/** 引数作成 */
		const opt = Object.assign({}, def, options)

		/** 対象ノードを収集 */
		const transItems = Translate.collectTranslateItem(opt.container)

		/** 対象ノードを翻訳処理 */
		const result = await Translate.translate(opt, transItems)

		/** イベント送信 */
		event.emit(EVENTS.END)

		/** コンソールログ */
		if (options.log) helper.consoleLog(result)

		return result
	},
	/**
	 * 文字列翻訳
	 * @param {TranslateApiOptions} options
	 * @param {string} text
	 * @returns {Promise<string>}
	 */
	text: async function (options, text) {
		const opt = Object.assign(
			{
				log: false,
				lang: 'jp',
				ext: 'json',
				name: undefined,
				elem: document,
				dynamic: false,
			},
			options,
		)

		const transItems = [
			new TranslateItem({
				type: NodeTypes.TextNode,
				elem: null,
				node: null,
				text,
				dynamic: opt.dynamic,
			}),
		]
		const result = await Translate.translate(opt, transItems)
		const output = _.chain(result).last().get('after').value() || text

		if (options.log) helper.consoleLog(result)
		return output
	},
}

export {EVENTS, SELECTOR, Translate, TranslateItem, NodeTypes, helper}
